import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  STARTING_FEN,
  getLocalStorage,
  setLocalStorage,
} from '@/Utils/CommonUtils';
import {
  buildNavIndex,
  getLineEnd,
  getNextMove,
  getPrevMove,
} from '../utils/moveNavigation';

type UseChapterNavigationArgs = {
  selectedChapter: any;
  movesList: Record<string, any[]>;
  moveSelected: any;
  setmoveSelected: (move: any) => void;
  // Conceal ("Hide next moves") only actually conceals if forward
  // navigation is blocked too - otherwise a student can reveal every
  // hidden move by mashing Next/Last/autoplay instead of playing them.
  isForwardNavBlocked: boolean;
  resetLessonUiState: () => void;
  // Extra screen-side cleanup when jumping back to the start position
  // (practice verdict snapshot + move counter live in the screen).
  onResetToStart: () => void;
};

// Path-aware navigation over the chapter tree, shared with the move
// list's keyboard bindings via utils/moveNavigation.ts: every entry point
// (buttons, arrows, autoplay) follows the line the user is on, exactly
// like lichess — never a flattened-DFS jump into a sibling branch.
// Also owns autoplay (interval + speed persistence), which is driven by
// the same handleNextMove.
export function useChapterNavigation({
  selectedChapter,
  movesList,
  moveSelected,
  setmoveSelected,
  isForwardNavBlocked,
  resetLessonUiState,
  onResetToStart,
}: UseChapterNavigationArgs) {
  const [isAutoplayOn, setIsAutoplayOn] = useState(false);
  // Lichess-style autoplay speeds (its replay menu's slow → fast range).
  // The chosen speed persists like the explorer/ceval configs.
  const [autoplaySpeed, setAutoplaySpeed] = useState(2000);
  const [showAutoplayMenu, setShowAutoplayMenu] = useState(false);

  const handleFirstMove = useCallback(() => {
    setIsAutoplayOn(false);
    setmoveSelected({
      move: '',
      fen: selectedChapter?.fen || STARTING_FEN,
      lastFen: selectedChapter?.fen || STARTING_FEN,
      id: 'start',
    } as any);
    resetLessonUiState();
    onResetToStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter?.fen, resetLessonUiState]);

  const chapterMoveTree = movesList[selectedChapter?.id] || [];
  const chapterNavIndex = useMemo(
    () => buildNavIndex(chapterMoveTree),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [movesList, selectedChapter?.id],
  );

  /** Decorates a tree node the way move-list clicks do, so the board gets
   * lastFen for its move highlight/animation. */
  const selectTreeNode = useCallback(
    (node: any) => {
      const prev = chapterNavIndex.get(String(node?.id))?.prev ?? null;
      setmoveSelected({
        ...node,
        newFen: node?.fen,
        lastFen: prev?.fen ?? selectedChapter?.fen,
        move: node?.san,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chapterNavIndex, selectedChapter?.fen],
  );

  // Move-list selections (click or arrow keys) route through this so any
  // manual jump pauses autoplay, as on lichess.
  const selectMoveFromList = useCallback(
    (move: any) => {
      setIsAutoplayOn(false);
      setmoveSelected(move);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handlePrevMove = useCallback(() => {
    // Any manual jump pauses autoplay, as on lichess.
    setIsAutoplayOn(false);
    const prev = getPrevMove(chapterNavIndex, moveSelected?.id ?? 'start');
    if (!prev) {
      return;
    }
    resetLessonUiState();
    if (prev === 'start') {
      handleFirstMove();
    } else {
      selectTreeNode(prev);
    }
  }, [
    moveSelected?.id,
    chapterNavIndex,
    handleFirstMove,
    selectTreeNode,
    resetLessonUiState,
  ]);

  // Steps one move forward along the current line. Returns false when there
  // is nothing to step to (line tip, or forward nav is blocked) so autoplay
  // can stop itself instead of spinning on a dead interval.
  const handleNextMove = useCallback(() => {
    if (isForwardNavBlocked) {
      return false;
    }
    const next = getNextMove(
      chapterMoveTree,
      chapterNavIndex,
      moveSelected?.id ?? 'start',
    );
    if (!next) {
      return false;
    }
    resetLessonUiState();
    selectTreeNode(next);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moveSelected?.id,
    chapterNavIndex,
    movesList,
    selectedChapter?.id,
    selectTreeNode,
    resetLessonUiState,
    isForwardNavBlocked,
  ]);

  const handleLastMove = useCallback(() => {
    setIsAutoplayOn(false);
    // See handleNextMove - jumping to the end would reveal every hidden
    // move at once, which defeats conceal mode even worse than Next would.
    if (isForwardNavBlocked) {
      return;
    }
    const end = getLineEnd(
      chapterMoveTree,
      chapterNavIndex,
      moveSelected?.id ?? 'start',
    );
    if (!end || end.id === moveSelected?.id) {
      return;
    }
    resetLessonUiState();
    selectTreeNode(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moveSelected?.id,
    chapterNavIndex,
    movesList,
    selectedChapter?.id,
    selectTreeNode,
    resetLessonUiState,
    isForwardNavBlocked,
  ]);

  // The autoplay interval closes over nothing: it always calls the CURRENT
  // handleNextMove through this ref, refreshed every render, so a long-
  // running interval can never step through a stale move tree.
  const nextMoveRef = useRef<any>(null);
  useEffect(() => {
    nextMoveRef.current = handleNextMove;
  });

  // Autoplay: step the current line at the chosen speed and stop at its tip
  // (handleNextMove returns false there), instead of leaving a dead
  // interval running — matching lichess's replay behavior.
  useEffect(() => {
    let intervalId: any = null;
    if (isAutoplayOn) {
      intervalId = setInterval(() => {
        if (nextMoveRef.current && nextMoveRef.current() === false) {
          setIsAutoplayOn(false);
        }
      }, autoplaySpeed);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAutoplayOn, autoplaySpeed]);

  // Restore the last autoplay speed once, then persist changes (same
  // pattern as the explorer/ceval configs).
  const autoplaySpeedLoadedRef = useRef(false);
  useEffect(() => {
    (async () => {
      const stored = (await getLocalStorage('study:autoplay-speed')) as
        | number
        | null;
      if (typeof stored === 'number' && stored >= 250 && stored <= 10000) {
        setAutoplaySpeed(stored);
      }
      autoplaySpeedLoadedRef.current = true;
    })();
  }, []);
  useEffect(() => {
    if (autoplaySpeedLoadedRef.current) {
      setLocalStorage('study:autoplay-speed', autoplaySpeed);
    }
  }, [autoplaySpeed]);

  // Autoplay would otherwise spin uselessly (handleNextMove already no-ops
  // when forward nav is blocked) if it was left on while switching into a
  // concealed/lesson/practice chapter - stop it outright instead of leaving
  // a dead interval running.
  useEffect(() => {
    if (isForwardNavBlocked) {
      setIsAutoplayOn(false);
    }
  }, [isForwardNavBlocked, selectedChapter?.id]);

  return {
    chapterNavIndex,
    selectTreeNode,
    selectMoveFromList,
    handleFirstMove,
    handlePrevMove,
    handleNextMove,
    handleLastMove,
    isAutoplayOn,
    setIsAutoplayOn,
    autoplaySpeed,
    setAutoplaySpeed,
    showAutoplayMenu,
    setShowAutoplayMenu,
  };
}
